/**
 * The single, exhaustive **value-kind authoring registry** (ADR-0008 §4) — the
 * one place that decides, per `ValueKind`, whether the LLM authoring surface may
 * define a trait member of that kind, and (when it may) how to coerce the
 * model's per-kind extra args, what declaration fields the committed
 * `pack.define_predicate` carries, and how to describe it in a change summary.
 *
 * WHY a registry keyed by `Record<ValueKind, …>`: the mapped type FORCES an
 * entry for every member of the {@link ValueKind} union — a newly-added kind
 * will not compile until its descriptor exists here. That closes the drift the
 * old hand-written value-kind lists left open: the four authoring surfaces
 * (`tools.ts` `define_trait`, the `ProposedTraitMember` artifact schema, the
 * `newMemberDeclaration` commit builder, and the authoring prompt) all DERIVE
 * from this registry rather than repeating their own kind list.
 *
 * `enum` is the FIRST registry entry, not a special case: the enum coercion that
 * used to live inline in `tools.ts` (`coerceEnumValues`) is now the enum
 * descriptor's {@link ValueKindAuthoringDescriptor.coerceExtra}. `ref` rides the
 * same seam (its `ref_types` → the declaration's `ref_roots`), and every future
 * per-kind extra is added by populating a descriptor.
 *
 * Everything here is JSON-serialisable and pure — no Maps / Dates / class
 * instances leak into the artifact.
 */
import { ENTITY_ROOTS, type EntityRoot } from './pack.js';
import {
  ValueKindSchema,
  type EnumValueDeclaration,
  type PredicateDeclaration,
  type ValueKind,
} from './predicate.js';
import { mintVocabId } from './vocab-id.js';

// ── per-kind extra args (the model-supplied side-channel) ────────────────────

/**
 * The union of per-kind "extra" args a trait member may carry beyond its
 * `value_kind`. Flat + optional so it round-trips through JSON and so an
 * authorable kind with no extra (text / number / dates / …) simply carries none:
 *
 *   - `enum_values` — the closed option set for an `enum` member. Each option's
 *     opaque `enm_…` id is MINTED here (the LLM never types one — ADR-0004 §1);
 *     the model supplies only display names.
 *   - `ref_types` — the entity roots a `ref` member may point at (→ the
 *     declaration's `ref_roots`). A closed subset of {@link EntityRoot}.
 *
 * A new per-kind extra is added by growing this shape AND the owning descriptor
 * — both changes land in one place.
 */
export interface KindExtra {
  readonly enum_values?: readonly EnumValueDeclaration[];
  readonly ref_types?: readonly EntityRoot[];
}

/**
 * A structured, self-correctable coercion failure. Thrown by a descriptor's
 * {@link ValueKindAuthoringDescriptor.coerceExtra} when the model's per-kind
 * args are fundamentally the wrong shape (as opposed to merely empty, which the
 * commit-time auditor catches). Carries a machine `code` so the caller can map
 * it to a tool-level rejection.
 */
export class ValueKindAuthoringError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ValueKindAuthoringError';
  }
}

// ── the descriptor + registry ────────────────────────────────────────────────

/**
 * One row of the authoring registry — the complete authoring contract for a
 * single {@link ValueKind}. `authorable:false` rows MUST carry a `deferReason`
 * (why the interactive surface can't define this kind yet); `authorable:true`
 * rows MUST carry a `when` prompt hint. The three function hooks are only
 * meaningful for kinds that carry extra args (enum / ref today).
 */
export interface ValueKindAuthoringDescriptor {
  /** The kind this descriptor governs (mirrors the map key — the invariant test asserts it). */
  readonly kind: ValueKind;
  /** May the LLM authoring surface define a trait member of this kind? */
  readonly authorable: boolean;
  /** REQUIRED when `authorable:false` — why the interactive surface defers it. */
  readonly deferReason?: string;
  /** Prompt hint (authorable only) — when the model should pick this kind. */
  readonly when?: string;
  /**
   * Liberally coerce the model's per-kind extra args (`enum_values`, `ref_types`,
   * …) into a validated {@link KindExtra}. Tolerant of the wildly varying shapes
   * an open model emits (bare string lists, `{value,label}` objects, stringified
   * JSON of either). Returns the coerced extras (possibly empty — the auditor
   * catches a genuinely-missing required extra at commit) or throws a
   * {@link ValueKindAuthoringError} for an un-coercible shape.
   */
  readonly coerceExtra?: (raw: Record<string, unknown>) => KindExtra;
  /** The per-kind fields the committed `pack.define_predicate` declaration carries. */
  readonly toDeclarationFields?: (extra: KindExtra) => Partial<PredicateDeclaration>;
  /** A short human label for the change summary / UI (e.g. "enum (Poisoned, Corrupt)"). */
  readonly summarize?: (extra: KindExtra) => string;
}

// ── coercion helpers (open-model tolerance) ──────────────────────────────────

/**
 * Un-double-encode a value an open model emitted as a stringified JSON literal
 * (e.g. `enum_values` arriving as `"[\"Poisoned\"]"` instead of a real array).
 * Mirrors the `unwrapJson` in `tools.ts`; duplicated here so the registry is
 * self-contained (the shared package must not import the worker).
 */
function unwrapJson(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (t === '' || (t[0] !== '{' && t[0] !== '[')) return v;
  try {
    const parsed: unknown = JSON.parse(t);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    // Not JSON — keep the original; the shape check rejects it.
  }
  return v;
}

/**
 * Coerce an untrusted `enum_values` arg into `EnumValueDeclaration[]` (or
 * `undefined`). Tolerant of ALL of: a bare string list, `{value/id/name, label}`
 * objects, a localized-record label, or a stringified JSON of either. Each
 * option's opaque `enm_…` id is minted HERE; the display text becomes the
 * mandatory localized `label`. Empty / label-less entries are dropped.
 */
function coerceEnumValues(v: unknown): readonly EnumValueDeclaration[] | undefined {
  const uv = unwrapJson(v);
  if (!Array.isArray(uv)) return undefined;
  const out: EnumValueDeclaration[] = [];
  for (const rawEntry of uv) {
    const entry = unwrapJson(rawEntry);
    let label: string | undefined;
    if (typeof entry === 'string') {
      label = entry.trim();
    } else if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const o = entry as Record<string, unknown>;
      const rawLabel = o['label'] ?? o['value'] ?? o['name'] ?? o['id'];
      if (typeof rawLabel === 'string') label = rawLabel.trim();
      else if (rawLabel && typeof rawLabel === 'object' && !Array.isArray(rawLabel)) {
        const loc = rawLabel as Record<string, unknown>;
        const en = loc['en'];
        const first = Object.values(loc).find((x): x is string => typeof x === 'string');
        label = typeof en === 'string' ? en.trim() : first?.trim();
      }
    }
    if (label === undefined || label === '') continue;
    out.push({ id: mintVocabId('enum'), label: { en: label } });
  }
  return out.length > 0 ? out : undefined;
}

const ENTITY_ROOT_SET: ReadonlySet<string> = new Set(ENTITY_ROOTS);

/**
 * Coerce an untrusted `ref_types` arg into a `EntityRoot[]` (or `undefined`).
 * Tolerant of a bare string list or a stringified JSON list; every entry must be
 * a real {@link EntityRoot} (unknown roots are dropped — the LLM never invents a
 * root token). An empty result omits (an unconstrained ref accepts any root).
 */
function coerceRefTypes(v: unknown): readonly EntityRoot[] | undefined {
  const uv = unwrapJson(v);
  if (!Array.isArray(uv)) return undefined;
  const seen = new Set<string>();
  const out: EntityRoot[] = [];
  for (const rawEntry of uv) {
    const entry = unwrapJson(rawEntry);
    if (typeof entry !== 'string') continue;
    const token = entry.trim();
    if (!ENTITY_ROOT_SET.has(token) || seen.has(token)) continue;
    seen.add(token);
    out.push(token as EntityRoot);
  }
  return out.length > 0 ? out : undefined;
}

/** Title-case an entity root token for a human summary ("faction" → "Faction"). */
function displayRoot(root: string): string {
  return root.length === 0 ? root : root.charAt(0).toUpperCase() + root.slice(1);
}

/** Default-locale text of an `EnumValueDeclaration.label`. */
function enumLabelText(o: EnumValueDeclaration): string {
  const label = o.label;
  if (typeof label === 'string') return label;
  const en = label['en'];
  if (typeof en === 'string') return en;
  const first = Object.values(label).find((x): x is string => typeof x === 'string');
  return first ?? o.id;
}

// ── a compact descriptor builder to keep the map readable ────────────────────

/** Build an `authorable:true` descriptor with a mandatory `when` hint. */
function authorable(
  kind: ValueKind,
  when: string,
  extra?: Pick<ValueKindAuthoringDescriptor, 'coerceExtra' | 'toDeclarationFields' | 'summarize'>,
): ValueKindAuthoringDescriptor {
  return { kind, authorable: true, when, ...(extra ?? {}) };
}

/** Build an `authorable:false` descriptor with a mandatory `deferReason`. */
function deferred(kind: ValueKind, deferReason: string): ValueKindAuthoringDescriptor {
  return { kind, authorable: false, deferReason };
}

/**
 * THE registry (ADR-0008 §4). Every {@link ValueKind} appears exactly once — the
 * `Record<ValueKind, …>` type is what makes that exhaustive and drift-proof.
 */
export const VALUE_KIND_AUTHORING: Record<ValueKind, ValueKindAuthoringDescriptor> = {
  // ── plain authorable scalars ───────────────────────────────────────────────
  text: authorable('text', 'freeform prose or a short label'),
  rich_text: authorable('rich_text', 'long-form, formatted prose (markdown)'),
  number: authorable('number', 'a numeric degree, score, count, or measurement'),
  boolean: authorable('boolean', 'a yes/no flag'),
  color: authorable('color', 'an sRGB color swatch'),

  // ── enum: the first extra-carrying kind (the moved coercion) ───────────────
  enum: authorable(
    'enum',
    'a FIXED SET of named, non-numeric states — supply the options in enum_values, e.g. ["Poisoned","Corrupt","Lost"]',
    {
      coerceExtra: (raw) => {
        const enum_values = coerceEnumValues(raw['enum_values']);
        return enum_values !== undefined ? { enum_values } : {};
      },
      toDeclarationFields: (extra) =>
        extra.enum_values !== undefined && extra.enum_values.length > 0
          ? { enum_values: [...extra.enum_values] }
          : {},
      summarize: (extra) => {
        const labels = (extra.enum_values ?? []).map(enumLabelText);
        return labels.length > 0 ? `enum (${labels.join(', ')})` : 'enum';
      },
    },
  ),

  // ── ref: rides the same seam (ref_types → ref_roots) ───────────────────────
  ref: authorable(
    'ref',
    'a reference/link to another entity — supply the allowed entity roots in ref_types, e.g. ["character","faction"]',
    {
      coerceExtra: (raw) => {
        const ref_types = coerceRefTypes(raw['ref_types']);
        return ref_types !== undefined ? { ref_types } : {};
      },
      toDeclarationFields: (extra) =>
        extra.ref_types !== undefined && extra.ref_types.length > 0
          ? { ref_roots: [...extra.ref_types] }
          : {},
      summarize: (extra) => {
        const roots = (extra.ref_types ?? []).map(displayRoot);
        return roots.length > 0 ? `reference to ${roots.join(', ')}` : 'reference';
      },
    },
  ),

  // ── real-world date/time family (no extra declaration fields) ──────────────
  real_date: authorable('real_date', 'a real-world calendar date (no time of day)'),
  real_time: authorable('real_time', 'a real-world time of day (no date)'),
  real_datetime: authorable('real_datetime', 'a real-world date and time'),
  real_datetime_range: authorable('real_datetime_range', 'a real-world span between two instants'),

  // ── in-world (campaign-calendar) date/time family ──────────────────────────
  game_date: authorable('game_date', 'an in-world (campaign-calendar) date'),
  game_datetime: authorable('game_datetime', 'an in-world date and time of day'),
  game_time: authorable('game_time', 'an in-world time of day'),
  game_date_range: authorable('game_date_range', 'an in-world span between two dates'),
  game_datetime_range: authorable('game_datetime_range', 'an in-world span between two datetimes'),

  // ── deferred: not yet supported on the interactive authoring surface ───────
  asset: deferred(
    'asset',
    'media kinds need format/size (media) constraints and an upload authoring UX, not yet on the interactive surface.',
  ),
  image: deferred(
    'image',
    'media kinds need format/size (media) constraints and an upload authoring UX, not yet on the interactive surface.',
  ),
  file: deferred(
    'file',
    'media kinds need format/size (media) constraints and an upload authoring UX, not yet on the interactive surface.',
  ),
  tuple: deferred(
    'tuple',
    'structured multi-field records need a tuple_fields authoring UX (per-field kind/enum/ref), not yet built.',
  ),
  geometry_point: deferred(
    'geometry_point',
    'geography kinds need parent-frame coordinate authoring, not yet on the interactive surface.',
  ),
  geometry_line: deferred(
    'geometry_line',
    'geography kinds need parent-frame coordinate authoring, not yet on the interactive surface.',
  ),
  geometry_polygon: deferred(
    'geometry_polygon',
    'geography kinds need parent-frame coordinate authoring, not yet on the interactive surface.',
  ),
  interior_frame: deferred(
    'interior_frame',
    'a renderable coordinate space nests a grid definition and is a geography-editor concern, not yet built.',
  ),
  containment: deferred(
    'containment',
    'multi-context containment nests a parent ref + a discriminated geometry position, not yet on the interactive surface.',
  ),
  recurrence_rule: deferred(
    'recurrence_rule',
    'calendar recurrence rules need anchor/cycle authoring (the @dl/calendar runtime), not yet built.',
  ),
};

// ── derived views (the four surfaces read these, never a hand-written list) ──

/** The kinds the LLM authoring surface may define, in {@link ValueKindSchema} order. */
export const AUTHORABLE_VALUE_KINDS: readonly ValueKind[] = ValueKindSchema.options.filter(
  (k) => VALUE_KIND_AUTHORING[k].authorable,
);

/** The kinds explicitly deferred (with a `deferReason`), in schema order. */
export const DEFERRED_VALUE_KINDS: readonly ValueKind[] = ValueKindSchema.options.filter(
  (k) => !VALUE_KIND_AUTHORING[k].authorable,
);

/** Is `kind` an authorable {@link ValueKind}? Narrows an untrusted string. */
export function isAuthorableValueKind(kind: string): kind is ValueKind {
  return (
    Object.prototype.hasOwnProperty.call(VALUE_KIND_AUTHORING, kind) &&
    VALUE_KIND_AUTHORING[kind as ValueKind].authorable
  );
}

/**
 * Coerce the model's per-kind extra args for a member declaring `kinds`, by
 * dispatching each authorable kind's {@link ValueKindAuthoringDescriptor.coerceExtra}
 * over the raw arg bag and merging the results. A kind with no `coerceExtra`
 * (plain scalar) contributes nothing; a non-authorable kind is ignored here (the
 * caller rejects it separately). The merged {@link KindExtra} is flat, so a
 * member may carry both (a hypothetical `enum` + `ref` polymorphic member).
 */
export function coerceValueKindExtra(
  kinds: readonly string[],
  raw: Record<string, unknown>,
): KindExtra {
  let extra: KindExtra = {};
  for (const kind of kinds) {
    if (!isAuthorableValueKind(kind)) continue;
    const descriptor = VALUE_KIND_AUTHORING[kind];
    if (descriptor.coerceExtra) extra = { ...extra, ...descriptor.coerceExtra(raw) };
  }
  return extra;
}

/**
 * The per-kind declaration fields for a member declaring `kinds`, merged across
 * every kind's {@link ValueKindAuthoringDescriptor.toDeclarationFields}. This is
 * the single builder the commit path uses (enum→`enum_values`, ref→`ref_roots`,
 * …) instead of an enum-only branch.
 */
export function valueKindDeclarationFields(
  kinds: readonly ValueKind[],
  extra: KindExtra,
): Partial<PredicateDeclaration> {
  let fields: Partial<PredicateDeclaration> = {};
  for (const kind of kinds) {
    const descriptor = VALUE_KIND_AUTHORING[kind];
    if (descriptor.toDeclarationFields) {
      fields = { ...fields, ...descriptor.toDeclarationFields(extra) };
    }
  }
  return fields;
}

/**
 * A short human label for a member's kind detail (the change summary / UI). Uses
 * the primary kind's {@link ValueKindAuthoringDescriptor.summarize} when it has
 * one (enum / ref carry option / root detail); otherwise falls back to the bare
 * kind token. Returns `undefined` for an empty kind list.
 */
export function summarizeValueKind(
  kinds: readonly ValueKind[],
  extra: KindExtra,
): string | undefined {
  const primary = kinds[0];
  if (primary === undefined) return undefined;
  const descriptor = VALUE_KIND_AUTHORING[primary];
  return descriptor.summarize ? descriptor.summarize(extra) : primary;
}

/**
 * The authoring prompt / tool-schema value_kind guidance, GENERATED from the
 * authorable descriptors' `when` hints (never a hand-written list — a new
 * authorable kind's guidance appears the moment its descriptor does). One line
 * per authorable kind: `["enum"] — a FIXED SET of named…`.
 */
export function authorableValueKindGuidance(): string {
  return AUTHORABLE_VALUE_KINDS.map(
    (k) => `["${k}"] — ${VALUE_KIND_AUTHORING[k].when ?? ''}`,
  ).join('\n');
}
